import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Lottie from "lottie-react";
import ReactConfetti from "react-confetti";
import { useWindowSize } from "react-use";
import successAnimation from "../../assets/success.json";
import pendingAnimation from "../../assets/pending.json";
import failedAnimation from "../../assets/failed.json";
import doctorIcon from "../../assets/doctor_icon.svg";

const PAYMENT_API_URL =
  process.env.REACT_APP_PAYMENT_API_URL ||""

// ── helpers ──────────────────────────────────────────────────────────────────

function copyToClipboard(text) {
  navigator.clipboard?.writeText(text).catch(() => {});
}

// ── sub-components ────────────────────────────────────────────────────────────

function DecorativeBlobs({ variant }) {
  const colors =
    variant === "success"
      ? ["#004276", "#2E7D5E", "#0077B6", "#00897B"]
      : variant === "pending"
      ? ["#b45309", "#d97706", "#92400e", "#fbbf24"]
      : ["#991b1b", "#b91c1c", "#7f1d1d", "#ef4444"];

  return (
    <>
      <div
        className="absolute -top-24 -left-24 w-80 h-80 rounded-full opacity-20 blur-3xl animate-pulse"
        style={{ background: colors[0] }}
      />
      <div
        className="absolute -bottom-24 -right-24 w-96 h-96 rounded-full opacity-15 blur-3xl animate-pulse"
        style={{ background: colors[1], animationDelay: "1s" }}
      />
      <div
        className="absolute top-1/2 -right-16 w-48 h-48 rounded-full opacity-10 blur-2xl animate-pulse"
        style={{ background: colors[2], animationDelay: "2s" }}
      />
      <div
        className="absolute bottom-1/3 -left-16 w-40 h-40 rounded-full opacity-10 blur-2xl animate-pulse"
        style={{ background: colors[3], animationDelay: "0.5s" }}
      />
    </>
  );
}

function TxnPill({ txnId, badgeText }) {
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    copyToClipboard(txnId);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  
}

function LoadingScreen() {
  return (
    <div
      className="flex flex-col justify-center items-center h-screen relative overflow-hidden"
      style={{ background: "linear-gradient(135deg, #001f3f 0%, #004276 50%, #0077B6 100%)" }}
    >
      <DecorativeBlobs variant="success" />
      <div className="relative z-10 flex flex-col items-center gap-6">
        <div className="relative w-20 h-20">
          <div className="absolute inset-0 rounded-full border-4 border-white/10" />
          <div
            className="absolute inset-0 rounded-full border-4 border-transparent border-t-white animate-spin"
            style={{ borderTopColor: "#3cba84" }}
          />
          <div className="absolute inset-3 rounded-full flex items-center justify-center"
            style={{ background: "rgba(255,255,255,0.05)" }}>
            <svg className="w-7 h-7 text-white/60" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
        </div>
        <div className="text-center">
          <p className="text-white text-lg font-semibold">Verifying Payment</p>
          <p className="text-white/50 text-sm mt-1">Please wait, this may take a moment…</p>
        </div>
        <div className="flex gap-1.5">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="w-2 h-2 rounded-full bg-white/40 animate-bounce"
              style={{ animationDelay: `${i * 0.15}s` }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// ── main component ────────────────────────────────────────────────────────────

function CurebayOrderStatus() {
  const navigate = useNavigate();
  const location = useLocation();
  const { width, height } = useWindowSize();

  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [orderData, setOrderData] = useState({});

  useEffect(() => {
    // Try location.search first; fall back to window.location.search (handles some gateway redirects)
    const searchStr = location.search || window.location.search;
    const params = new URLSearchParams(searchStr);

    // Payment gateways use various param names — try all common ones
    const orderId =
      params.get("orderId") ||
      params.get("order_id") ||
      params.get("orderid") ||
      params.get("OrderId") ||
      params.get("merchantOrderId") ||
      params.get("txnid") ||
      params.get("txn_id");

    console.log("[CurebayOrderStatus] searchStr:", searchStr, "orderId:", orderId, "all params:", Object.fromEntries(params));

    if (!orderId) {
      setLoading(false);
      setPaymentStatus("fail");
      return;
    }

    const fetchOrderStatus = async () => {
      try {
        const res = await fetch(
          `${PAYMENT_API_URL}/payment/check-status?orderId=${orderId}`,
          { method: "GET", headers: { accept: "application/json" } }
        );
        const result = await res.json();
        if (res.ok && result) {
          const d = result.data || result;
          const raw = (d.orderStatus || d.paymentStatus || d.status || "").toLowerCase();
          const status =
            raw === "success" || raw === "completed" || raw === "captured"
              ? "success"
              : raw === "pending" || raw === "initiated"
              ? "pending"
              : "fail";
          let appt = {};
          try {
            const saved = localStorage.getItem("appointmentDetails");
            if (saved) appt = JSON.parse(saved);
          } catch {}

          setPaymentStatus(status);
          setOrderData({
            patientName: d.patientName || d.customerName || appt.patientName || "Patient",
            txnId: d.transactionId || d.txnId || d.bankTxnId || orderId || "",
            amount: d.amount || d.totalAmount || d.txnAmount || "",
            serviceName: d.serviceType || d.serviceName || d.service || "",
            orderId: d.orderId || orderId || "",
            paymentMode: d.paymentMode || "",
            date: d.updatedAt || d.createdAt || d.txnDate || "",
            doctorName: d.doctorName || appt.doctorName || "",
            hospitalName: d.hospitalName || d.clinicName || "",
            doctorPhoto: appt.doctorPhoto || "",
            specialization: appt.specialization || "",
            appointmentNo: appt.appointmentNo || "",
            appointmentDate: appt.appointmentDate || "",
            appointmentTime: appt.appointmentTime || "",
          });
          localStorage.setItem("paymentStatus", status);
          localStorage.setItem("trancationid", d.transactionId || d.txnId || "");
          if (status === "success") localStorage.removeItem("appointmentDetails");
        } else {
          setPaymentStatus("fail");
        }
      } catch {
        setPaymentStatus("fail");
      } finally {
        setLoading(false);
      }
    };

    fetchOrderStatus();
  }, [location.search]);

  if (loading) return <LoadingScreen />;

  const isSuccess = paymentStatus === "completed" || paymentStatus === "success";
  const isPending = paymentStatus === "pending";
  const isFail = !isSuccess && !isPending;

  const config = isSuccess
    ? {
        bg: "linear-gradient(135deg, #001f3f 0%, #004276 45%, #0D7377 100%)",
        accentBg: "linear-gradient(135deg, #004276, #2E7D5E)",
        animation: successAnimation,
        badgeBg: "rgba(52,211,153,0.15)",
        badgeBorder: "rgba(52,211,153,0.4)",
        badgeText: "#34d399",
        badgeLabel: "Payment Successful",
        title: "Booking Confirmed!",
        subtitle: `${orderData.patientName || "there"}! Your appointment has been successfully booked ${orderData.doctorName ? ` with Dr. ${orderData.doctorName}` : ""}.`,
        btnPrimary: "linear-gradient(90deg, #004276 0%, #2E7D5E 100%)",
        btnSecondary: "rgba(255,255,255,0.08)",
        confettiColors: ["#34d399", "#059669", "#6ee7b7", "#a7f3d0", "#004276", "#0077B6"],
        icon: "✓",
        iconBg: "rgba(52,211,153,0.2)",
        iconColor: "#34d399",
      }
    : isPending
    ? {
        bg: "linear-gradient(135deg, #1c0a00 0%, #78350f 45%, #b45309 100%)",
        accentBg: "linear-gradient(135deg, #92400e, #d97706)",
        animation: pendingAnimation,
        badgeBg: "rgba(251,191,36,0.15)",
        badgeBorder: "rgba(251,191,36,0.4)",
        badgeText: "#fbbf24",
        badgeLabel: "Payment Pending",
        title: "Awaiting Confirmation",
        subtitle: `Hi ${orderData.patientName || "there"}, your payment is being processed. We'll notify you once confirmed.`,
        btnPrimary: "linear-gradient(90deg, #92400e 0%, #d97706 100%)",
        btnSecondary: "rgba(255,255,255,0.08)",
        confettiColors: [],
        icon: "⏳",
        iconBg: "rgba(251,191,36,0.2)",
        iconColor: "#fbbf24",
      }
    : {
        bg: "linear-gradient(135deg, #1a0000 0%, #7f1d1d 45%, #991b1b 100%)",
        accentBg: "linear-gradient(135deg, #7f1d1d, #dc2626)",
        animation: failedAnimation,
        badgeBg: "rgba(248,113,113,0.15)",
        badgeBorder: "rgba(248,113,113,0.4)",
        badgeText: "#f87171",
        badgeLabel: "Payment Failed",
        title: "Payment Unsuccessful",
        subtitle: `Sorry ${orderData.patientName || "there"}, we couldn't process your payment. No amount has been deducted.`,
        btnPrimary: "linear-gradient(90deg, #991b1b 0%, #dc2626 100%)",
        btnSecondary: "rgba(255,255,255,0.08)",
        confettiColors: [],
        icon: "✕",
        iconBg: "rgba(248,113,113,0.2)",
        iconColor: "#f87171",
      };

  const variant = isSuccess ? "success" : isPending ? "pending" : "fail";

  return (
    <div
      className="flex justify-center items-center min-h-screen relative overflow-hidden py-8 px-4"
      style={{ background: config.bg }}
    >
      {/* Decorative blobs */}
      <DecorativeBlobs variant={variant} />

      {/* Confetti for success */}
      {isSuccess && (
        <ReactConfetti
          width={width}
          height={height}
          recycle={false}
          numberOfPieces={350}
          gravity={0.15}
          colors={config.confettiColors}
        />
      )}

      {/* Grid texture overlay */}
      <div
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage:
            "repeating-linear-gradient(0deg,transparent,transparent 40px,rgba(255,255,255,0.3) 40px,rgba(255,255,255,0.3) 41px),repeating-linear-gradient(90deg,transparent,transparent 40px,rgba(255,255,255,0.3) 40px,rgba(255,255,255,0.3) 41px)",
        }}
      />

      {/* Main card */}
      <div
        className="relative z-10 w-full max-w-md rounded-3xl overflow-hidden shadow-2xl"
        style={{
          background: "rgba(255,255,255,0.06)",
          backdropFilter: "blur(24px)",
          border: "1px solid rgba(255,255,255,0.12)",
          boxShadow: "0 32px 64px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.1)",
        }}
      >
        {/* Top accent bar */}
        <div className="h-1 w-full" style={{ background: config.accentBg }} />

        {/* Header section */}
        <div className="px-6 pt-8 pb-4 flex flex-col items-center">
          {/* CureBay branding */}
          <div className="flex items-center gap-2 mb-6">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center font-bold text-white text-sm"
              style={{ background: config.accentBg }}
            >
              CB
            </div>
            <span className="text-white/70 text-sm font-medium tracking-wide">CureBay Health</span>
          </div>

          {/* Lottie animation */}
          <div
            className="rounded-full p-1 mb-4"
            style={{
              background: "rgba(255,255,255,0.05)",
              border: "1px solid rgba(255,255,255,0.1)",
            }}
          >
            <Lottie
              animationData={config.animation}
              autoplay
              loop={isPending}
              style={{ height: 120, width: 120 }}
            />
          </div>

          {/* Status badge */}
          <div
            className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold mb-4"
            style={{
              background: config.badgeBg,
              border: `1px solid ${config.badgeBorder}`,
              color: config.badgeText,
            }}
          >
            <span
              className="w-1.5 h-1.5 rounded-full animate-pulse"
              style={{ background: config.badgeText }}
            />
            {config.badgeLabel}
          </div>

          <h1 className="text-2xl font-bold text-white text-center leading-tight mb-2">
            {config.title}
          </h1>
          <p className="text-white/55 text-sm text-center leading-relaxed px-2">
            {config.subtitle}
          </p>
        </div>

        {/* Transaction ID pill */}
        {orderData.txnId && (
          <TxnPill txnId={orderData.txnId} accentBg={config.accentBg} badgeText={config.badgeText} />
        )}

        {/* Appointment Details Card */}
        {(isSuccess || isPending) && (orderData.doctorName || orderData.appointmentNo) && (
          <div className="mx-5 mb-5 rounded-2xl overflow-hidden bg-white">
            {orderData.doctorName && (
              <div className="flex items-center gap-3 p-4">
                {orderData.doctorPhoto ? (
                  <img
                    src={orderData.doctorPhoto}
                    alt="Doctor"
                    className="w-16 h-16 rounded-xl object-cover flex-shrink-0"
                    onError={(e) => { e.target.onerror = null; e.target.src = doctorIcon; }}
                  />
                ) : (
                  <div
                    className="w-16 h-16 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: "linear-gradient(135deg, #E0F2FE, #BAE6FD)" }}
                  >
                    <img src={doctorIcon} alt="Doctor" className="w-10 h-10 object-contain" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-gray-900 font-bold text-base leading-tight">Dr.{orderData.doctorName}</p>
                  {orderData.specialization && (
                    <p className="text-[#0077B6] text-sm mt-0.5 leading-snug">{orderData.specialization}</p>
                  )}
                </div>
              </div>
            )}
            <div className="border-t border-gray-100 mx-4" />
            <div className="p-4 flex flex-col gap-3">
              {orderData.patientName && orderData.patientName !== "Patient" && (
                <div>
                  <p className="text-gray-900 font-bold text-sm mb-1">Patient Details</p>
                  <p className="text-gray-500 text-sm">{orderData.patientName}</p>
                </div>
              )}
              <div className="flex items-start justify-between gap-4">
                {(orderData.appointmentDate || orderData.appointmentTime) && (
                  <div>
                    <p className="text-gray-900 font-bold text-sm mb-1">Consultation Date &amp; Time</p>
                    <div className="flex items-center gap-2 text-gray-500 text-sm">
                      {orderData.appointmentDate && (
                        <span>
                          {new Date(orderData.appointmentDate).toLocaleDateString("en-GB", {
                            day: "numeric", month: "short", year: "numeric",
                          })}
                        </span>
                      )}
                      {orderData.appointmentDate && orderData.appointmentTime && (
                        <span className="text-gray-300">|</span>
                      )}
                      {orderData.appointmentTime && <span>{orderData.appointmentTime}</span>}
                    </div>
                  </div>
                )}
                {orderData.appointmentNo && (
                  <div className="text-right">
                    <p className="text-gray-900 font-bold text-sm mb-1">Appointment No</p>
                    <p className="text-gray-500 text-sm">{orderData.appointmentNo}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div className="px-5 pb-7 flex flex-col gap-3">
          {isFail ? (
            <>
              <button
                onClick={() => navigate(-1)}
                className="w-full py-3.5 rounded-xl text-white font-semibold text-sm transition-all hover:scale-[1.02] active:scale-[0.98] hover:shadow-lg"
                style={{ background: config.btnPrimary }}
              >
                Try Again
              </button>
              <button
                onClick={() => navigate("/dashboard")}
                className="w-full py-3 rounded-xl text-white/70 font-medium text-sm transition-all hover:text-white hover:bg-white/10"
                style={{
                  background: config.btnSecondary,
                  border: "1px solid rgba(255,255,255,0.1)",
                }}
              >
                Back to Home
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => navigate("/dashboard")}
                className="w-full py-3.5 rounded-xl text-white font-semibold text-sm transition-all hover:scale-[1.02] active:scale-[0.98] hover:shadow-lg"
                style={{ background: config.btnPrimary }}
              >
                Go to Dashboard
              </button>
              
            </>
          )}
        </div>

        {/* Support footer */}
     
      </div>
    </div>
  );
}

export default CurebayOrderStatus;
